import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useId, type Ref } from "react";
import { useMe } from "../../lib/me";
import { cx } from "../../ui";
import { Icon, IconButton, type IconName } from "../../ui/icons";
import { useSignOut } from "../auth/session";
import { ApprovalsNavLink } from "../approvals/ApprovalsNavLink";
import { useCurrentWorkspaceId } from "./current-workspace";
import { PageNav } from "./PageNav";
import { keptView, SECTIONS, sectionOf } from "./sections";

const linkBase =
  "flex min-h-9 items-center gap-3 rounded-md text-sm text-text hover:bg-surface-2 aria-[current=page]:bg-surface-2 aria-[current=page]:font-semibold";

const ICONS: Record<(typeof SECTIONS)[number]["key"], IconName> = { inbox: "inbox", tasks: "tasks", chat: "chat", dashboard: "dashboard" };

const ALL = "all";

/**
 * The left sidebar. Below `md` it is a drawer (always full width); from `md` it can be collapsed
 * to a rail of icons (`collapsed`), where labels stay for screen readers and show as tooltips.
 */
export function Sidebar({
  open,
  collapsed,
  onToggleCollapsed,
  firstLinkRef,
}: {
  open: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  firstLinkRef: Ref<HTMLSelectElement>;
}) {
  const me = useMe();
  const signOut = useSignOut();
  // Collapsed applies from `md` only: the phone drawer always shows labels.
  const hide = collapsed ? "md:sr-only" : "";
  const linkClass = cx(linkBase, collapsed ? "px-3 md:justify-center md:px-0" : "px-3");

  const workspaceId = useCurrentWorkspaceId();
  const location = useRouterState({ select: (s) => s.location });
  const pathname = location.pathname;
  // The one picker (architecture §1): a workspace, or "All workspaces", which is the inbox
  // across every account (`?scope=all`). Other sections are per workspace, so All opens the inbox.
  const all = /^\/w\/[^/]+\/inbox\/?$/.test(pathname) && (location.search as Record<string, unknown>)["scope"] === "all";
  const navigate = useNavigate();
  const switcherId = useId();
  const workspaceName = all ? "All workspaces" : me.workspaces.find((w) => w.id === workspaceId)?.name;

  return (
    <aside
      id="sidebar"
      aria-label="Sidebar"
      className={cx(
        "fixed inset-y-0 left-0 z-40 flex w-64 flex-col gap-6 border-r border-border bg-surface p-4",
        collapsed && "md:w-16 md:px-2",
        "transition-transform md:sticky md:top-0 md:visible md:h-dvh md:translate-x-0",
        open ? "translate-x-0" : "invisible -translate-x-full",
      )}
    >
      <div className={cx("flex items-center justify-between gap-2", collapsed && "md:justify-center")}>
        <p className={cx("px-3 text-base font-semibold text-text", hide)}>KitWork+</p>
        <IconButton
          icon="panel"
          label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          aria-controls="sidebar"
          className="hidden md:inline-grid"
          onClick={onToggleCollapsed}
        />
      </div>

      {collapsed ? (
        <button
          type="button"
          onClick={onToggleCollapsed}
          title={workspaceName === undefined ? "Workspace" : `Workspace: ${workspaceName}. Expand to switch.`}
          aria-label={workspaceName === undefined ? "Workspace" : `Workspace: ${workspaceName}. Expand the sidebar to switch.`}
          className="mx-auto hidden size-9 place-items-center rounded-md bg-surface-2 text-sm font-semibold text-text md:grid"
        >
          {(workspaceName ?? "?").charAt(0).toUpperCase()}
        </button>
      ) : null}
      <div className={cx("flex flex-col gap-1 px-1", collapsed && "md:hidden")}>
        <label htmlFor={switcherId} className="px-2 text-xs font-medium uppercase tracking-wide text-muted">
          Workspace
        </label>
        <select
          id={switcherId}
          ref={firstLinkRef}
          value={all ? ALL : workspaceId === null ? "" : workspaceId}
          onChange={(e) => {
            const choice = e.target.value;
            // On Mail the view (People, Receipts, GitHub…) carries over; the rest starts fresh.
            const view = keptView(pathname, location.search);
            if (choice === ALL) {
              if (workspaceId !== null) void navigate({ to: "/w/$workspaceId/inbox", params: { workspaceId }, search: { scope: "all", ...view } });
              return;
            }
            void navigate({ to: sectionOf(pathname).to, params: { workspaceId: choice }, ...(sectionOf(pathname).key === "inbox" ? { search: view } : {}) });
          }}
          className="min-h-9 rounded-md border border-border bg-surface px-2 text-sm text-text"
        >
          <option value={ALL}>All workspaces</option>
          {me.workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </div>

      <nav aria-label="Main" className="flex min-h-0 flex-1 flex-col gap-1">
        {/* The sections and the page's own list scroll on their own (the page behind does not move);
            Approvals, Settings and the profile stay pinned below however long the list gets. */}
        <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain px-1">
          {workspaceId === null
            ? null
            : SECTIONS.map((s) => (
                <Link key={s.key} to={s.to} params={{ workspaceId }} className={linkClass} title={s.label}>
                  <Icon name={ICONS[s.key]} />
                  <span className={hide}>{s.label}</span>
                </Link>
              ))}
          {workspaceId === null ? null : <PageNav workspaceId={workspaceId} collapsed={collapsed} />}
        </div>
        <div className="flex shrink-0 flex-col gap-1">
          <ApprovalsNavLink className={linkClass} icon={<Icon name="approvals" />} labelClass={hide} />
          <Link to="/settings/workspaces" className={linkClass} title="Settings">
            <Icon name="settings" />
            <span className={hide}>Settings</span>
          </Link>
          <div className={cx("mt-2 flex items-center gap-2 border-t border-border pt-3", collapsed ? "px-1 md:flex-col md:px-0" : "px-1")}>
            <Link
              to="/settings/profile"
              title={`${me.name} · ${me.email}`}
              className="grid size-8 shrink-0 place-items-center rounded-full bg-surface-2 text-sm font-semibold text-text"
            >
              <span aria-hidden="true">{me.name.trim().charAt(0).toUpperCase()}</span>
              <span className="sr-only">Your profile</span>
            </Link>
            <div className={cx("min-w-0 flex-1", hide)}>
              <p className="truncate text-sm font-medium text-text">{me.name}</p>
              <p className="truncate text-xs text-muted">{me.email}</p>
            </div>
            <IconButton icon="signOut" label="Sign out" onClick={() => void signOut()} />
          </div>
        </div>
      </nav>
    </aside>
  );
}
